import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Seo from '../../components/Seo';
import Input from '../../components/ui/Input';
import ImageUploadZone from './components/ImageUploadZone';
import StarRatingSelector from './components/StarRatingSelector';
import VisibilityToggle from './components/VisibilityToggle';
import ChallengeContextCard from './components/ChallengeContextCard';
import UploadProgressModal from './components/UploadProgressModal';
import SuccessModal from './components/SuccessModal';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { makeSafeObjectPath, uploadToBucket } from '../../utils/storage';
import { compressImageUnderBytes } from '../../utils/imageCompression';

const ProofSubmission = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useAuth();
  const challengeId = location?.state?.challengeId || location?.state?.challengeData?.id || '';

  const [challengeData, setChallengeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    rating: 0,
    isPublic: true,
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedProof, setSubmittedProof] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!user?.id || !challengeId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const { data: ch, error: chErr } = await supabase
        .from('ch_challenges')
        .select('id, title, goal_type, target_count, total_goals, meta')
        .eq('id', challengeId)
        .maybeSingle();

      if (chErr || !ch) {
        setChallengeData(null);
        setIsLoading(false);
        return;
      }

      const { count, error: countErr } = await supabase
        .from('ch_proofs')
        .select('id', { count: 'exact', head: true })
        .eq('challenge_id', challengeId)
        .eq('user_id', user.id);

      if (countErr) {
        setChallengeData(null);
        setIsLoading(false);
        return;
      }

      const currentCount = count || 0;
      const goalType = ch.goal_type === 'thirty-day' ? 'thirty_day' : ch.goal_type;
      const parameters = ch?.meta?.parameters || {};

      const targetCount = (() => {
        if (goalType === 'fixed') return Number(ch.target_count || 0);
        if (goalType === 'split') return Number(ch.total_goals || 0);
        if (goalType === 'thirty_day') return 30;
        return Number(ch.total_goals || ch.target_count || 0);
      })();

      setChallengeData({
        id: ch.id,
        name: ch.title,
        goalType,
        currentDay: goalType === 'thirty_day' ? currentCount : 0,
        currentCount,
        targetCount,
        splitCount: Number(parameters?.splitCount || 0),
        currentProofNumber: currentCount + 1,
      });

      setIsLoading(false);
    };

    load();
  }, [challengeId, user?.id]);

  const handleImageSelect = (image, error) => {
    setSelectedImage(image);
    if (error) {
      setErrors((prev) => ({ ...prev, image: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors?.image;
        return newErrors;
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors?.[name];
        return newErrors;
      });
    }
  };

  const handleRatingChange = (rating) => {
    setFormData((prev) => ({ ...prev, rating }));
    if (errors?.rating) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors?.rating;
        return newErrors;
      });
    }
  };

  const handleVisibilityChange = (isPublic) => {
    setFormData((prev) => ({ ...prev, isPublic }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedImage) {
      newErrors.image = '증명 이미지를 업로드해주세요';
    }

    if (!formData?.title?.trim()) {
      newErrors.title = '제목을 입력해주세요';
    } else if (formData?.title?.trim()?.length < 2) {
      newErrors.title = '제목은 최소 2자 이상이어야 합니다';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = '설명을 입력해주세요';
    } else if (formData?.description?.trim()?.length < 10) {
      newErrors.description = '설명은 최소 10자 이상이어야 합니다';
    }

    if (formData?.rating === 0) {
      newErrors.rating = '달성도를 평가해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!user?.id) {
      alert('로그인이 필요합니다');
      navigate('/login-registration');
      return;
    }

    if (!challengeData?.id) {
      alert('챌린지 정보를 불러오지 못했습니다');
      return;
    }

    if (!validateForm()) {
      const firstErrorKey = Object.keys(errors)?.[0];
      const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
      if (errorElement) {
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      setUploadProgress(15);

      const file = selectedImage?.file;
      if (!file) {
        setErrors((prev) => ({ ...prev, image: '증명 이미지를 업로드해주세요' }));
        setIsUploading(false);
        return;
      }

      let blob = file;
      if (file.size > 1024 * 1024) {
        blob = await compressImageUnderBytes(file, 1024 * 1024);
      }

      const path = makeSafeObjectPath({
        userId: user.id,
        originalName: file?.name,
        prefix: 'proofs',
      });

      setUploadProgress(45);

      const { error: uploadError } = await uploadToBucket({
        bucket: 'proof-images',
        path,
        blob,
        contentType: blob?.type || 'image/jpeg',
        cacheControl: '31536000',
      });

      if (uploadError) {
        setErrors((prev) => ({ ...prev, image: uploadError?.message || '이미지 업로드에 실패했습니다' }));
        setIsUploading(false);
        return;
      }

      setUploadProgress(75);

      const { data: inserted, error: insertError } = await supabase
        .from('ch_proofs')
        .insert({
          challenge_id: challengeData.id,
          user_id: user.id,
          number: challengeData.currentProofNumber,
          title: formData?.title?.trim(),
          description: formData?.description?.trim(),
          rating: formData?.rating,
          is_public: !!formData?.isPublic,
          image_path: path,
        })
        .select('*')
        .single();

      if (insertError) {
        setErrors((prev) => ({ ...prev, form: insertError?.message || '증명 저장에 실패했습니다' }));
        setIsUploading(false);
        return;
      }

      setUploadProgress(100);

      setSubmittedProof({
        id: inserted?.id,
        challengeId: inserted?.challenge_id,
        proofNumber: inserted?.number,
        title: inserted?.title,
        description: inserted?.description,
        rating: inserted?.rating,
        isPublic: inserted?.is_public,
        image: selectedImage?.preview,
        imageAlt: `Proof submission image for ${formData?.title} showing achievement progress`,
        createdAt: inserted?.submitted_at,
      });

      setIsUploading(false);
      setShowSuccessModal(true);
    } catch (err) {
      setErrors((prev) => ({ ...prev, form: err?.message || '제출 중 오류가 발생했습니다' }));
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    if (
      selectedImage ||
      formData?.title ||
      formData?.description ||
      formData?.rating > 0
    ) {
      const confirmCancel = window.confirm(
        '작성 중인 내용이 있습니다. 정말 취소하시겠습니까?'
      );
      if (!confirmCancel) return;
    }
    navigate('/challenge-detail', { state: { challengeId: challengeData?.id } });
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo 
        title="증명 제출 - 도전 기록 | 꿈현.Labs" 
        description="꿈현.Labs에서 챌린지 증명을 제출하고 도전 기록을 남기세요. 목표 달성 과정을 기록하고 성과를 공유하여 꿈을 현실로 만들어보세요."
        canonicalPath="/proof-submission" 
      />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 pb-32 lg:pb-12">
        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
          <div className="space-y-3 md:space-y-4">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
              증명 제출
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
              챌린지 달성을 증명할 이미지와 내용을 작성해주세요
            </p>
          </div>

          {isLoading ? (
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            </div>
          ) : challengeData ? (
            <ChallengeContextCard challengeData={challengeData} />
          ) : (
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <p className="text-sm text-muted-foreground caption">챌린지 정보를 불러오지 못했습니다.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
            <ImageUploadZone
              onImageSelect={handleImageSelect}
              selectedImage={selectedImage}
              error={errors?.image}
            />

            <Input
              label="증명 제목"
              type="text"
              name="title"
              placeholder="예: 오늘의 운동 완료"
              value={formData?.title}
              onChange={handleInputChange}
              error={errors?.title}
              required
              maxLength={50}
              description="증명 내용을 간단히 요약해주세요 (최대 50자)"
            />

            <div>
              <label className="text-sm md:text-base font-medium text-foreground mb-2 block">
                증명 설명 <span className="text-error">*</span>
              </label>
              <textarea
                name="description"
                value={formData?.description}
                onChange={handleInputChange}
                placeholder="오늘의 운동 내용, 느낀 점, 어려웠던 점 등을 자유롭게 작성해주세요"
                rows={6}
                maxLength={500}
                className={`w-full px-4 py-3 bg-background border rounded-md text-sm md:text-base text-foreground placeholder:text-muted-foreground transition-gaming focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
                  errors?.description
                    ? 'border-error focus:ring-error' :'border-border'
                }`}
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs md:text-sm text-muted-foreground caption">
                  최소 10자 이상 작성해주세요
                </span>
                <span className="text-xs md:text-sm text-muted-foreground data-text">
                  {formData?.description?.length}/500
                </span>
              </div>
              {errors?.description && (
                <p className="text-sm text-error mt-2 caption">{errors?.description}</p>
              )}
            </div>

            <StarRatingSelector
              value={formData?.rating}
              onChange={handleRatingChange}
              error={errors?.rating}
            />

            <VisibilityToggle
              isPublic={formData?.isPublic}
              onChange={handleVisibilityChange}
            />

            {errors?.form && (
              <p className="text-sm text-error mt-2 caption">{errors?.form}</p>
            )}

            <div className="hidden lg:flex items-center justify-end space-x-4 pt-6 border-t border-border">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-muted text-foreground rounded-md font-medium transition-gaming hover:bg-muted/80 active:translate-y-px"
              >
                취소
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-gaming hover:shadow-gaming-md active:translate-y-px flex items-center space-x-2"
              >
                <span>제출하기</span>
              </button>
            </div>
          </form>
        </div>
      </main>
      <div className="lg:hidden">
      </div>
      {isUploading && (
        <UploadProgressModal
          progress={uploadProgress}
          onCancel={() => {
            setIsUploading(false);
            setUploadProgress(0);
          }}
        />
      )}
      {showSuccessModal && submittedProof && (
        <SuccessModal
          proofData={submittedProof}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
    </div>
  );
};

export default ProofSubmission;